import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useToast } from '../components/ui/Toast';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import {
  AVAILABLE_MODELS,
  DEFAULT_AGENT_SETTINGS,
  ATHENA_DEFAULT_PROMPT,
  HERMES_DEFAULT_PROMPT,
  DAEDALUS_FARGATE_DEFAULT_PROMPT,
  DAEDALUS_RUNNER_DEFAULT_PROMPT,
  PROMETHEUS_DEFAULT_PROMPT,
  DEFAULT_ARGUS_PROMPT,
  GTM_ASSET_KINDS,
  GTM_DEFAULT_PROMPTS,
} from '@pantheon/shared';
import type { AgentSettings, ReleaseTrack, ReleaseTaskType, GtmAssetKind } from '@pantheon/shared';

/** Defaults shown in the prompt fields when no custom prompt is saved */
const DEFAULT_PROMPTS: Record<'athena' | 'hermes' | 'daedalus' | 'prometheus' | 'argus', string> = {
  athena: ATHENA_DEFAULT_PROMPT,
  hermes: HERMES_DEFAULT_PROMPT,
  daedalus: DAEDALUS_FARGATE_DEFAULT_PROMPT,
  prometheus: PROMETHEUS_DEFAULT_PROMPT,
  argus: DEFAULT_ARGUS_PROMPT,
};

const tabs = [
  { id: 'ai', label: 'AI Settings' },
  { id: 'tracks', label: 'Release Tracks' },
];

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState('ai');

  return (
    <div className="animate-rise">
      <PageHeader
        mark="S"
        markBg="bg-primary-light"
        markText="text-primary"
        title="Settings"
      />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'ai' && <AISettingsTab />}
      {activeTab === 'tracks' && <ReleaseTracksTab />}
    </div>
  );
}

// --- AI Settings Tab ---

function AISettingsTab() {
  const { get, put } = useApi();
  const { toast } = useToast();
  const [settings, setSettings] = useState<AgentSettings>(DEFAULT_AGENT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gtmKind, setGtmKind] = useState<GtmAssetKind>('positioning-doc');
  // Live model list from the Anthropic account (refreshed ~daily server-side);
  // falls back to the static list bundled in shared
  const [models, setModels] = useState<{ id: string; name: string }[]>([...AVAILABLE_MODELS]);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    const result = await get<AgentSettings>('/settings');
    if (result.success && result.data) {
      // Athena was added after some settings were saved — seed from Hermes if absent
      setSettings({
        ...result.data,
        athena: result.data.athena ?? {
          model: result.data.hermes.model,
          maxTokens: Math.max(result.data.hermes.maxTokens, 8192),
        },
        // Argus was added later — seed from Athena/Hermes if absent
        argus: result.data.argus ?? {
          model: (result.data.athena ?? result.data.hermes).model,
          maxTokens: 8192,
          maxIterations: 5,
        },
      });
    }
    setLoading(false);
  }, [get]);

  const fetchModels = useCallback(async () => {
    const result = await get<{ id: string; name: string }[]>('/models');
    if (result.success && result.data?.length) setModels(result.data);
  }, [get]);

  useEffect(() => { fetchSettings(); fetchModels(); }, [fetchSettings, fetchModels]);

  const handleSave = async () => {
    setSaving(true);
    const result = await put<AgentSettings>('/settings', settings);
    if (result.success) toast('Settings saved', 'success');
    else toast(result.error?.message ?? 'Failed to save', 'error');
    setSaving(false);
  };

  const agents = [
    { key: 'athena' as const, name: 'Athena', title: 'The Overseer', color: 'border-l-sky-500' },
    { key: 'hermes' as const, name: 'Hermes', title: 'The Messenger', color: 'border-l-primary' },
    { key: 'daedalus' as const, name: 'Daedalus', title: 'The Craftsman', color: 'border-l-green-500' },
    { key: 'prometheus' as const, name: 'Prometheus', title: 'The Fire Bringer', color: 'border-l-orange-500' },
    { key: 'argus' as const, name: 'Argus', title: 'The Watchman', color: 'border-l-red-500' },
  ];

  const select = 'w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

  // Max Tokens is an API-only knob (Claude Code / the runner sets its own output length).
  // Daedalus runs on the local runner — hide it. Hermes/Athena only bill the API when the
  // Planning Engine is "api"; Prometheus comms tasks only bill the API when its Engine is "api".
  const showMaxTokens = (key: 'athena' | 'hermes' | 'daedalus' | 'prometheus' | 'argus') => {
    if (key === 'daedalus' || key === 'argus') return false; // both run on the local runner (subscription)
    if (key === 'prometheus') return (settings.prometheus.runnerMode ?? 'local') === 'api';
    return (settings.hermes.runnerMode ?? 'api') === 'api'; // hermes + athena share the Planning Engine
  };

  if (loading) return <p className="text-gray-500 text-sm">Loading settings...</p>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={handleSave} loading={saving} size="sm">Save Settings</Button>
      </div>

      <div className="space-y-4">
        {agents.map((agent) => (
          <div key={agent.key} className={`bg-white rounded-2xl border border-slate-100 border-l-4 ${agent.color} shadow-sm shadow-slate-200/50 p-6`}>
            <div className="flex items-center gap-3 mb-4">
              <h3 className="text-lg font-semibold">{agent.name}</h3>
              <span className="text-sm text-gray-500 italic">{agent.title}</span>
            </div>

            <div className={`grid ${agent.key === 'daedalus' ? 'grid-cols-3' : 'grid-cols-2'} gap-4 mb-4`}>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {agent.key === 'daedalus' ? 'Research Model' : 'AI Model'}
                </label>
                <select
                  className={select}
                  value={settings[agent.key].model}
                  onChange={(e) => setSettings({ ...settings, [agent.key]: { ...settings[agent.key], model: e.target.value } })}
                >
                  {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              {agent.key === 'daedalus' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Implementation Model</label>
                  <select
                    className={select}
                    value={(settings.daedalus as any).loopModel ?? settings.daedalus.model}
                    onChange={(e) => setSettings({ ...settings, daedalus: { ...settings.daedalus, loopModel: e.target.value } as any })}
                  >
                    {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
              )}
              {agent.key === 'daedalus' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1" title="When the primary model's weekly cap is hit, runners switch to this model for the rest of the session instead of pausing.">Fallback Model</label>
                  <select
                    className={select}
                    value={(settings.daedalus as any).fallbackModel ?? ''}
                    onChange={(e) => setSettings({ ...settings, daedalus: { ...settings.daedalus, fallbackModel: e.target.value || undefined } as any })}
                  >
                    <option value="">None (pause &amp; wait)</option>
                    {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
              )}
              {agent.key === 'daedalus' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1" title="In the unified Jira space, a ticket assigned to this account in Ready is picked up by the runner fleet. Blank turns that board watch off.">Runner Jira Account ID</label>
                  <input
                    className={`${select} w-80`}
                    value={(settings.daedalus as any).jiraRunnerAccountId ?? ''}
                    onChange={(e) => setSettings({ ...settings, daedalus: { ...settings.daedalus, jiraRunnerAccountId: e.target.value.trim() || undefined } as any })}
                    placeholder="Jira accountId (blank = unified board watch off)"
                  />
                </div>
              )}
              {showMaxTokens(agent.key) && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Max Tokens</label>
                  <input
                    type="number"
                    className={`${select} w-40`}
                    value={settings[agent.key].maxTokens}
                    onChange={(e) => setSettings({ ...settings, [agent.key]: { ...settings[agent.key], maxTokens: parseInt(e.target.value) || 4096 } })}
                  />
                </div>
              )}
            </div>

            {agent.key === 'hermes' && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Planning Engine</label>
                <select
                  className={`${select} w-96`}
                  value={settings.hermes.runnerMode ?? 'api'}
                  onChange={(e) => setSettings({ ...settings, hermes: { ...settings.hermes, runnerMode: e.target.value as 'api' | 'local' | 'local_only' } })}
                >
                  <option value="api">Claude API (tokens)</option>
                  <option value="local">Local Runner — API fallback if unserved</option>
                  <option value="local_only">Local Runner only — fail instead of falling back</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Applies to Hermes and Athena planning calls. "Local Runner only" fails the call if no runner
                  serves it (retry manually) — the API is never billed.
                </p>
              </div>
            )}

            {agent.key === 'daedalus' && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Runner Mode</label>
                <select
                  className={`${select} w-80`}
                  value={settings.daedalus.runnerMode ?? 'fargate'}
                  onChange={(e) => setSettings({ ...settings, daedalus: { ...settings.daedalus, runnerMode: e.target.value as 'fargate' | 'local' } })}
                >
                  <option value="fargate">Cloud — ECS Fargate + Claude API</option>
                  <option value="local">Local Runner — Claude subscription</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Local mode queues sessions for a daedalus-runner daemon on your machine instead of launching Fargate.
                </p>
              </div>
            )}

            {agent.key === 'athena' && (
              <p className="text-xs text-gray-500 mb-3">
                Athena runs wherever the Hermes <strong>Planning Engine</strong> is set (currently shared with Hermes).
              </p>
            )}

            {agent.key === 'prometheus' && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Engine (comms tasks)</label>
                <select
                  className={`${select} w-96`}
                  value={settings.prometheus.runnerMode ?? 'local'}
                  onChange={(e) => setSettings({ ...settings, prometheus: { ...settings.prometheus, runnerMode: e.target.value as 'api' | 'local' | 'local_only' } })}
                >
                  <option value="api">Claude API (tokens)</option>
                  <option value="local">Local Runner — API fallback if unserved</option>
                  <option value="local_only">Local Runner only — fail instead of falling back</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Applies to comms tasks (Slack / Confluence / email). GTM <strong>asset</strong> tasks always run on
                  the runner — they need the build, screenshot, and Drive environment.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {agent.key === 'daedalus' ? 'System Prompt — Cloud (Fargate + API)' : 'System Prompt'}
                <span className="text-xs text-gray-500 font-normal ml-2">Prefilled with the default — clearing the field restores it</span>
              </label>
              <textarea
                className={`${select} resize-y font-mono text-xs`}
                rows={8}
                value={settings[agent.key].systemPrompt ?? DEFAULT_PROMPTS[agent.key]}
                onChange={(e) => setSettings({ ...settings, [agent.key]: { ...settings[agent.key], systemPrompt: e.target.value || undefined } })}
              />
            </div>

            {agent.key === 'daedalus' && (
              <div className="mt-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  System Prompt — Local Runner (Claude Code)
                  <span className="text-xs text-gray-500 font-normal ml-2">Prefilled with the default — clearing the field restores it</span>
                </label>
                <textarea
                  className={`${select} resize-y font-mono text-xs`}
                  rows={8}
                  value={settings.daedalus.runnerSystemPrompt ?? DAEDALUS_RUNNER_DEFAULT_PROMPT}
                  onChange={(e) => setSettings({ ...settings, daedalus: { ...settings.daedalus, runnerSystemPrompt: e.target.value || undefined } })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Appended to Claude Code's own system prompt. The repo's CLAUDE.md is loaded automatically — no need to instruct reading it.
                  Avoid double quotes in this prompt.
                </p>
              </div>
            )}

            {agent.key === 'prometheus' && (
              <>
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Drive Folder ID (GTM deliverables)</label>
                  <input
                    className={`${select} w-96`}
                    value={settings.prometheus.driveFolderId ?? ''}
                    onChange={(e) => setSettings({ ...settings, prometheus: { ...settings.prometheus, driveFolderId: e.target.value || undefined } })}
                    placeholder="Google Drive folder ID"
                  />
                  <p className="text-xs text-gray-500 mt-1">Each Athena plan / release gets its own subfolder under this folder; a document's images go in "&lt;Document name&gt; images" inside it.</p>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Brand kit folder ID (logos + brand guidelines PDF)</label>
                  <input
                    className={`${select} w-96`}
                    value={settings.prometheus.brandingFolderId ?? ''}
                    onChange={(e) => setSettings({ ...settings, prometheus: { ...settings.prometheus, brandingFolderId: e.target.value || undefined } })}
                    placeholder="Google Drive folder ID"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Downloaded into <code>./branding/</code> for every GTM asset job — the model reads the guidelines and uses the real logo files.
                    Generated (non-screenshot) images use OpenAI via the runner's <code>OPENAI_API_KEY</code> env var or the Secrets Manager entry <code>pantheon/openai-api-key</code> (<code>{'{'}"key": "…"{'}'}</code>).
                  </p>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">GTM Asset Prompts</label>
                  <div className="flex items-center gap-2 mb-2">
                    <select
                      className={`${select} w-72`}
                      value={gtmKind}
                      onChange={(e) => setGtmKind(e.target.value as GtmAssetKind)}
                    >
                      {GTM_ASSET_KINDS.map((k) => <option key={k} value={k}>{k.replace(/-/g, ' ')}</option>)}
                    </select>
                    {!!settings.prometheus.gtmPrompts?.[gtmKind] && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const next = { ...(settings.prometheus.gtmPrompts ?? {}) };
                          delete next[gtmKind];
                          setSettings({ ...settings, prometheus: { ...settings.prometheus, gtmPrompts: next } });
                        }}
                      >
                        Reset to default
                      </Button>
                    )}
                  </div>
                  <textarea
                    className={`${select} resize-y font-mono text-xs`}
                    rows={10}
                    value={settings.prometheus.gtmPrompts?.[gtmKind] || GTM_DEFAULT_PROMPTS[gtmKind]}
                    onChange={(e) => setSettings({ ...settings, prometheus: { ...settings.prometheus, gtmPrompts: { ...settings.prometheus.gtmPrompts, [gtmKind]: e.target.value } } })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Edits are saved with Settings and used by runner-produced deliverables.
                  </p>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Release Tracks Tab ---

function ReleaseTracksTab() {
  const { get, post, put, del } = useApi();
  const { toast } = useToast();
  const [tracks, setTracks] = useState<ReleaseTrack[]>([]);
  const [editing, setEditing] = useState<Partial<ReleaseTrack> | null>(null);
  const [saving, setSaving] = useState(false);

  const input = 'w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

  useEffect(() => {
    get<ReleaseTrack[]>('/prometheus/tracks').then((r) => {
      if (r.success && r.data) setTracks(r.data);
    });
  }, [get]);

  const emptyTask = (): any => ({
    department: '', type: 'slack_message', name: '', config: {}, promptTemplate: '',
  });

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    if (editing.trackId) {
      await put(`/prometheus/tracks/${editing.trackId}`, editing);
    } else {
      await post('/prometheus/tracks', editing);
    }
    const r = await get<ReleaseTrack[]>('/prometheus/tracks');
    if (r.success && r.data) setTracks(r.data);
    setEditing(null);
    setSaving(false);
    toast('Track saved', 'success');
  };

  const renderTaskList = (
    tasks: any[],
    phase: 'preReleaseTasks' | 'postReleaseTasks',
    label: string,
  ) => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-semibold text-gray-700">{label}</label>
        <Button variant="ghost" size="sm" onClick={() => setEditing({ ...editing, [phase]: [...(editing?.[phase] ?? []), emptyTask()] })}>+ Add Task</Button>
      </div>
      {tasks.length === 0 && (
        <p className="text-xs text-gray-500 italic mb-2">No tasks — click + Add Task</p>
      )}
      {tasks.map((task: any, i: number): any => (
        <div key={i} className="border border-gray-100 rounded-lg p-4 mb-2 bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500">Task {i + 1}</span>
            <button className="text-xs text-danger hover:underline" onClick={() => setEditing({ ...editing, [phase]: tasks.filter((_: any, j: number) => j !== i) })}>Remove</button>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-2">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-0.5">Department</label>
              <input className={input} value={task.department ?? ''} onChange={(e) => {
                const updated = [...tasks]; updated[i] = { ...updated[i], department: e.target.value }; setEditing({ ...editing, [phase]: updated });
              }} placeholder="Support, Marketing, Sales" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-0.5">Type</label>
              <select className={input} value={task.type ?? 'slack_message'} onChange={(e) => {
                const updated = [...tasks]; updated[i] = { ...updated[i], type: e.target.value as ReleaseTaskType }; setEditing({ ...editing, [phase]: updated });
              }}>
                <option value="slack_message">Slack Message</option>
                <option value="confluence_update">Confluence Update</option>
                <option value="email_document">Email Document</option>
                <option value="gtm_asset">GTM Asset (runner)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-0.5">Name</label>
              <input className={input} value={task.name ?? ''} onChange={(e) => {
                const updated = [...tasks]; updated[i] = { ...updated[i], name: e.target.value }; setEditing({ ...editing, [phase]: updated });
              }} placeholder="Post release note" />
            </div>
          </div>

          {task.type === 'slack_message' && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-0.5">Channel</label>
              <input className={input} value={task.config?.channel ?? ''} onChange={(e) => {
                const updated = [...tasks]; updated[i] = { ...updated[i], config: { ...updated[i].config, channel: e.target.value } }; setEditing({ ...editing, [phase]: updated });
              }} placeholder="#releases" />
            </div>
          )}

          {task.type === 'confluence_update' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-0.5">Space Key</label>
                <input className={input} value={task.config?.spaceKey ?? ''} onChange={(e) => {
                  const updated = [...tasks]; updated[i] = { ...updated[i], config: { ...updated[i].config, spaceKey: e.target.value } }; setEditing({ ...editing, [phase]: updated });
                }} placeholder="HELP" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-0.5">Search/Title</label>
                <input className={input} value={task.config?.searchQuery ?? ''} onChange={(e) => {
                  const updated = [...tasks]; updated[i] = { ...updated[i], config: { ...updated[i].config, searchQuery: e.target.value } }; setEditing({ ...editing, [phase]: updated });
                }} placeholder="Article title to update" />
              </div>
            </div>
          )}

          {task.type === 'email_document' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-0.5">Recipients (comma-separated)</label>
                <input className={input} value={(task.config?.recipients ?? []).join(', ')} onChange={(e) => {
                  const updated = [...tasks]; updated[i] = { ...updated[i], config: { ...updated[i].config, recipients: e.target.value.split(',').map((s: string) => s.trim()) } }; setEditing({ ...editing, [phase]: updated });
                }} placeholder="marketing@co.com, sales@co.com" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-0.5">Subject</label>
                <input className={input} value={task.config?.subject ?? ''} onChange={(e) => {
                  const updated = [...tasks]; updated[i] = { ...updated[i], config: { ...updated[i].config, subject: e.target.value } }; setEditing({ ...editing, [phase]: updated });
                }} placeholder="Release Update" />
              </div>
            </div>
          )}

          {task.type === 'gtm_asset' && (
            <div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-0.5">Asset Kind</label>
                  <select className={input} value={task.config?.assetKind ?? ''} onChange={(e) => {
                    const updated = [...tasks]; updated[i] = { ...updated[i], config: { ...updated[i].config, assetKind: e.target.value } }; setEditing({ ...editing, [phase]: updated });
                  }}>
                    <option value="">Select asset kind...</option>
                    {GTM_ASSET_KINDS.map((k) => <option key={k} value={k}>{k.replace(/-/g, ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-0.5">Drive folder override (optional)</label>
                  <input className={input} value={task.config?.driveFolderId ?? ''} onChange={(e) => {
                    const updated = [...tasks]; updated[i] = { ...updated[i], config: { ...updated[i].config, driveFolderId: e.target.value || undefined } }; setEditing({ ...editing, [phase]: updated });
                  }} placeholder="Defaults to Settings folder" />
                </div>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-1.5 text-xs text-gray-600">
                  <input type="checkbox" checked={!!task.config?.requiresApproval} onChange={(e) => {
                    const updated = [...tasks]; updated[i] = { ...updated[i], config: { ...updated[i].config, requiresApproval: e.target.checked } }; setEditing({ ...editing, [phase]: updated });
                  }} />
                  Requires approval (pause the run)
                </label>
                <label className="flex items-center gap-1.5 text-xs text-gray-600">
                  <input type="checkbox" checked={!!task.config?.captureScreenshots} onChange={(e) => {
                    const updated = [...tasks]; updated[i] = { ...updated[i], config: { ...updated[i].config, captureScreenshots: e.target.checked } }; setEditing({ ...editing, [phase]: updated });
                  }} />
                  Capture app screenshots
                </label>
              </div>
            </div>
          )}

          <div className="mt-2">
            <label className="block text-xs font-semibold text-gray-600 mb-0.5">Prompt Template</label>
            <textarea className={`${input} resize-y`} rows={2} value={task.promptTemplate ?? ''} onChange={(e) => {
              const updated = [...tasks]; updated[i] = { ...updated[i], promptTemplate: e.target.value }; setEditing({ ...editing, [phase]: updated });
            }} placeholder={task.type === 'gtm_asset' ? 'Additional direction (optional — appended to the asset prompt)' : 'Write a concise release note...'} />
          </div>
        </div>
      ))}
    </div>
  );

  const handleDelete = async (trackId: string) => {
    if (!confirm('Delete this track?')) return;
    await del(`/prometheus/tracks/${trackId}`);
    setTracks((prev) => prev.filter((t) => t.trackId !== trackId));
    toast('Track deleted', 'info');
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button size="sm" onClick={() => setEditing({ name: '', description: '', preReleaseTasks: [], postReleaseTasks: [] })}>
          Add Track
        </Button>
      </div>

      {tracks.length === 0 && !editing && (
        <div className="text-center py-8 text-gray-500 bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50">
          <p>No release tracks configured yet.</p>
        </div>
      )}

      <div className="space-y-3">
        {tracks.map((track) => (
          <div key={track.trackId} className="bg-white rounded-2xl border border-slate-100 border-l-4 border-l-orange-500 shadow-sm shadow-slate-200/50 p-4 flex items-center justify-between">
            <div>
              <h3 className="font-semibold">{track.name}</h3>
              <p className="text-sm text-gray-500">{track.description}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {(track.preReleaseTasks ?? []).map((t, i) => (
                  <span key={`pre-${i}`} className="text-xs bg-blue-50 text-blue-600 px-2.5 py-0.5 rounded-full">
                    Pre: {t.department} — {t.type.replace('_', ' ')}
                  </span>
                ))}
                {(track.postReleaseTasks ?? []).map((t, i) => (
                  <span key={`post-${i}`} className="text-xs bg-orange-50 text-orange-600 px-2.5 py-0.5 rounded-full">
                    Post: {t.department} — {t.type.replace('_', ' ')}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="secondary" size="sm" onClick={() => setEditing(track)}>Edit</Button>
              <Button variant="danger" size="sm" onClick={() => handleDelete(track.trackId!)}>Delete</Button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="mt-4 bg-white rounded-2xl border border-slate-100 shadow-[0_1px_2px_rgba(15,23,42,.04),0_10px_30px_-14px_rgba(15,23,42,.18)] p-6">
          <h3 className="text-lg font-semibold mb-4">{editing.trackId ? 'Edit Track' : 'New Track'}</h3>

          <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
          <input className={`${input} mb-3`} value={editing.name ?? ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Bug Fix" />

          <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
          <input className={`${input} mb-4`} value={editing.description ?? ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Single bug fix release" />

          {renderTaskList(editing.preReleaseTasks ?? [], 'preReleaseTasks', 'Pre-Release Tasks')}
          <div className="border-t border-gray-200 my-4" />
          {renderTaskList(editing.postReleaseTasks ?? [], 'postReleaseTasks', 'Post-Release Tasks')}

          <div className="flex gap-2 mt-4">
            <Button onClick={handleSave} loading={saving}>Save Track</Button>
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
